import React, {useState, useEffect, useMemo} from 'react';
import {Box, Stack} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {alpha, styled} from '@mui/material/styles';
import {
  Body,
  Headline,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import LanguageSelector, {
  LanguageGroup,
  LanguageOption,
} from '@contentlab/components/feature-editor/components/language-selector';
import LanguageCode from '@contentlab/components/feature-editor/components/language-selector/LanguageCode';
import SelectionLanguagesCounter from '@contentlab/components/feature-editor/components/language-selector/SelectionLanguagesCounter';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {
  DraftCaptionsProject,
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {FeatureName} from '@contentlab/types/project';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {
  getCaptionsSourceLanguageCreditTooltipSeen,
  setCaptionsSourceLanguageCreditTooltipSeen,
} from '@/utils/LtidStorage';
import translate from '@/utils/translate';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import useCaptionsLanguagesQuery from '@/hooks/content-studio/use-captions-languages-query';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets';

const checkboxCheckedSrc = '/assets/svg/checkbox-checked.svg';
const checkboxUncheckedSrc = '/assets/svg/checkbox-unchecked.svg';

const LanguagesContentContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
}));

const SourceLanguagePaddingContainer = styled(Box)(() => ({
  paddingLeft: '24px',
  paddingRight: '24px',
}));

const LanguageSelectorContainer = styled(Box)(() => ({
  flex: 1,
  overflow: 'auto',
  minHeight: 0,
  marginTop: '16px',
}));

const SourceLanguagePanelContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{isSelected: boolean}>(({isSelected}) => ({
  display: 'flex',
  height: '56px',
  padding: '8px 16px',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
  borderRadius: 'var(--radius-sm, 12px)',
  border: `1px solid ${designSystemToken('semantic.bg.brand')}`,
  backgroundColor: isSelected
    ? alpha(designSystemToken('semantic.bg.brand-secondary'), 0.5)
    : designSystemToken('semantic.bg.transparent'),
  cursor: 'pointer',
}));

function SourceLanguageInfo({
  languageName,
  languageCode,
  isSelected,
}: {
  languageName: string;
  languageCode: string;
  isSelected: boolean;
}) {
  return (
    <Box display="flex" gap="8px" alignItems="center">
      <Body size="md">{languageName}</Body>
      <LanguageCode code={languageCode} isSelected={isSelected} />
    </Box>
  );
}

function SourceLanguageCheckbox({
  isChecked,
  onClick,
  showTooltip,
}: {
  isChecked: boolean;
  onClick: (e: React.MouseEvent) => void;
  showTooltip: boolean;
}) {
  return (
    <Tooltip
      open={showTooltip}
      placement="bottom-end"
      arrow
      type="light"
      slotProps={{
        tooltip: {
          sx: {
            width: '141px',
          },
        },
      }}
      title={
        <Body size="md">
          {translate(
            'You can choose to include it in your captions generation.'
          )}
        </Body>
      }
    >
      <Box
        width="22px"
        height="24px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{cursor: 'pointer'}}
        onClick={onClick}
      >
        <img
          src={isChecked ? checkboxCheckedSrc : checkboxUncheckedSrc}
          alt=""
        />
      </Box>
    </Tooltip>
  );
}

function SourceLanguageCreditsSection({
  credits,
  isChecked,
  showTooltip,
  onCheckboxClick,
  onTooltipClose,
}: {
  credits: number;
  isChecked: boolean;
  showTooltip: boolean;
  onCheckboxClick: (e: React.MouseEvent) => void;
  onTooltipClose: () => void;
}) {
  return (
    <ClickAwayListener onClickAway={onTooltipClose}>
      <Box display="flex" alignItems="center" gap="8px">
        <Body size="sm" whiteSpace="nowrap">
          {credits === 1
            ? translate('1 credit if selected')
            : translate('{credit} credits if selected', {
                credit: credits,
              })}
        </Body>
        <SourceLanguageCheckbox
          isChecked={isChecked}
          onClick={onCheckboxClick}
          showTooltip={showTooltip}
        />
      </Box>
    </ClickAwayListener>
  );
}

function SourceLanguagePanel({
  languageName,
  languageCode,
  isSelected,
  credits,
  showTooltip,
  onToggle,
  onCheckboxClick,
  onTooltipClose,
}: {
  languageName: string;
  languageCode: string;
  isSelected: boolean;
  credits: number;
  showTooltip: boolean;
  onToggle: () => void;
  onCheckboxClick: (e: React.MouseEvent) => void;
  onTooltipClose: () => void;
}) {
  return (
    <SourceLanguagePanelContainer isSelected={isSelected} onClick={onToggle}>
      <Stack flex={1} spacing={0.5} sx={{py: 0.5}}>
        <Headline size="xs">{translate('Source Language')}</Headline>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <SourceLanguageInfo
            languageName={languageName}
            languageCode={languageCode}
            isSelected={isSelected}
          />
          <SourceLanguageCreditsSection
            credits={credits}
            isChecked={isSelected}
            showTooltip={showTooltip}
            onCheckboxClick={onCheckboxClick}
            onTooltipClose={onTooltipClose}
          />
        </Stack>
      </Stack>
    </SourceLanguagePanelContainer>
  );
}

interface LanguagesContentProps {
  draftCaptionsProject: DraftCaptionsProject;
}

function LanguagesContent({draftCaptionsProject}: LanguagesContentProps) {
  const {uiInteraction} = useUiInteractionEvent();
  const {uploadedVideos} = useFeatureAssets(FeatureName.CAPTIONS);

  const getRequiredCredits = useMemo(() => {
    const matchingVideo = uploadedVideos.find(
      (video) => video.id === draftCaptionsProject.assetId
    );

    if (!matchingVideo) {
      return 0;
    }

    return calculateVideoTokensForAssets([matchingVideo]);
  }, [uploadedVideos, draftCaptionsProject.assetId]);

  const handleLanguagesChange = (languages: string[]) => {
    uiInteraction({
      item_name: 'choose_languages',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_LANGUAGES,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
      interaction_details: JSON.stringify({
        languages: languages.join(','),
      }),
    });
    contentLabActions.setDraftCaptionsProject({
      ...draftCaptionsProject,
      selectedLanguages: languages,
    });
  };
  const {data: captionLanguages = [], isLoading: isLoadingLanguages} =
    useCaptionsLanguagesQuery();

  const [isSourceLanguageSelected, setIsSourceLanguageSelected] =
    useState(false);

  const [showSourceLanguageCreditTooltip, setShowSourceLanguageCreditTooltip] =
    useState(!getCaptionsSourceLanguageCreditTooltipSeen());

  const languageGroups: LanguageGroup[] = useMemo(() => {
    const toLanguageOption = (langResponse: {
      name: string;
      code: string;
    }): LanguageOption => ({
      id: langResponse.name,
      label: langResponse.name,
      code: langResponse.code,
    });

    const originalLanguageOption: LanguageOption | null =
      draftCaptionsProject.apiOriginalLanguage
        ? {
            id: draftCaptionsProject.apiOriginalLanguage.name,
            label: draftCaptionsProject.apiOriginalLanguage.name,
            code: draftCaptionsProject.apiOriginalLanguage.code,
          }
        : null;

    const hardcodedPopularLanguageNames = [
      'English',
      'Spanish (Latin America)',
      'French (France)',
      'German',
      'Portuguese (Brazil)',
      'Hindi',
    ].map((lang) => lang.toLowerCase());

    let popularLanguages = captionLanguages
      .filter((lang) =>
        hardcodedPopularLanguageNames.includes(lang.name.toLowerCase())
      )
      .sort((a, b) => {
        const indexA = hardcodedPopularLanguageNames.indexOf(
          a.name.toLowerCase()
        );
        const indexB = hardcodedPopularLanguageNames.indexOf(
          b.name.toLowerCase()
        );
        return indexA - indexB;
      })
      .map(toLanguageOption);

    if (originalLanguageOption) {
      popularLanguages = popularLanguages.filter(
        (lang) => lang.code !== originalLanguageOption.code
      );
    }

    const allLanguages = [...captionLanguages]
      .filter(
        (lang) =>
          !originalLanguageOption || lang.code !== originalLanguageOption.code
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(toLanguageOption);

    const groups = [];

    groups.push(new LanguageGroup(translate('Popular'), popularLanguages, 2));
    groups.push(new LanguageGroup(translate('All'), allLanguages, 2));

    return groups;
  }, [captionLanguages, draftCaptionsProject.apiOriginalLanguage]);

  // Sync source language checkbox with selected languages
  useEffect(() => {
    const sourceLanguageId = draftCaptionsProject.apiOriginalLanguage?.name;
    if (sourceLanguageId) {
      setIsSourceLanguageSelected(
        draftCaptionsProject.selectedLanguages.includes(sourceLanguageId)
      );
    }
  }, [
    draftCaptionsProject.selectedLanguages,
    draftCaptionsProject.apiOriginalLanguage,
  ]);

  const toggleSourceLanguageSelection = () => {
    if (showSourceLanguageCreditTooltip) {
      setShowSourceLanguageCreditTooltip(false);
      setCaptionsSourceLanguageCreditTooltipSeen();
    }

    const newChecked = !isSourceLanguageSelected;
    setIsSourceLanguageSelected(newChecked);
    const sourceLanguageId = draftCaptionsProject.apiOriginalLanguage?.name;
    if (sourceLanguageId) {
      if (newChecked) {
        const newSelectedLanguages = [
          ...draftCaptionsProject.selectedLanguages,
        ];
        if (!newSelectedLanguages.includes(sourceLanguageId)) {
          newSelectedLanguages.push(sourceLanguageId);
          handleLanguagesChange(newSelectedLanguages);
        }
      } else {
        const newSelectedLanguages =
          draftCaptionsProject.selectedLanguages.filter(
            (lang) => lang !== sourceLanguageId
          );
        handleLanguagesChange(newSelectedLanguages);
      }
    }
  };

  const handleSourceLanguageCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleSourceLanguageSelection();
  };

  const handleSourceLanguageTooltipClose = () => {
    if (showSourceLanguageCreditTooltip) {
      setShowSourceLanguageCreditTooltip(false);
      setCaptionsSourceLanguageCreditTooltipSeen();
    }
  };

  const handleClearAllLanguages = () => {
    contentLabActions.updateDraftCaptionsProjectLanguages([]);
  };

  const handleSearchQueryCleared = () => {
    uiInteraction({
      item_name: 'clear_search_query',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_LANGUAGES,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
    });
  };

  const handleSearchQueryChanged = (query: string) => {
    uiInteraction({
      item_name: 'search_query_changed',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_LANGUAGES,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
      interaction_details: JSON.stringify({query}),
    });
  };

  const handleClearAllPressed = () => {
    uiInteraction({
      item_name: 'clear_all',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_LANGUAGES,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
    });
  };

  return (
    <LanguagesContentContainer>
      {draftCaptionsProject.apiOriginalLanguage?.code && (
        <SourceLanguagePaddingContainer>
          <SourceLanguagePanel
            languageName={draftCaptionsProject.apiOriginalLanguage.name}
            languageCode={draftCaptionsProject.apiOriginalLanguage.code}
            isSelected={isSourceLanguageSelected}
            credits={getRequiredCredits}
            showTooltip={showSourceLanguageCreditTooltip}
            onToggle={toggleSourceLanguageSelection}
            onCheckboxClick={handleSourceLanguageCheckboxClick}
            onTooltipClose={handleSourceLanguageTooltipClose}
          />
        </SourceLanguagePaddingContainer>
      )}
      <LanguageSelectorContainer>
        <LanguageSelector
          languageGroups={languageGroups}
          selectedLanguages={draftCaptionsProject.selectedLanguages}
          setSelectedLanguages={handleLanguagesChange}
          isLoading={isLoadingLanguages}
          title={translate('Add Translation')}
          hideCounterWhenNonSelected
          customSelectedLanguagesCounter={
            <SelectionLanguagesCounter
              selectedLanguages={draftCaptionsProject.selectedLanguages}
              handleClearAll={handleClearAllLanguages}
            />
          }
          searchQueryCleared={handleSearchQueryCleared}
          searchQueryChanged={handleSearchQueryChanged}
          clearAllPressed={handleClearAllPressed}
        />
      </LanguageSelectorContainer>
    </LanguagesContentContainer>
  );
}

export default LanguagesContent;
